import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Calculator, FileText, Download, Lock, Unlock, Save, Plus, Trash2, DollarSign
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ baseURL: API, withCredentials: true });
ax.interceptors.request.use((config) => {
  const token = localStorage.getItem("ng_admin_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const STATUSES = ["new", "pricing-required", "draft", "sent", "approved", "declined", "in-production"];
const statusColors = {
  new: "bg-[#FF5722]", "pricing-required": "bg-yellow-600", draft: "bg-blue-600",
  sent: "bg-purple-600", approved: "bg-green-600", declined: "bg-red-700", "in-production": "bg-teal-600",
};

function fmt(n) { return `$${(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }
function formatDate(iso) { if (!iso) return "-"; try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } }

export default function QuoteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [customItems, setCustomItems] = useState([]);
  const [discountAmt, setDiscountAmt] = useState(0);
  const [adminNotes, setAdminNotes] = useState("");
  const [locked, setLocked] = useState(false);
  const [editedLineItems, setEditedLineItems] = useState([]);
  const [marginOverride, setMarginOverride] = useState(null);

  const fetchQuote = useCallback(async () => {
    try {
      const res = await ax.get(`/admin/quotes/${id}`);
      setQuote(res.data);
      setPricing(res.data.calculated_pricing || null);
      setEditedLineItems(res.data.calculated_pricing?.line_items || []);
      setMarginOverride(res.data.calculated_pricing?.summary?.margin_pct ?? null);
      setCustomItems(res.data.pricing?.custom_line_items || []);
      setDiscountAmt(res.data.pricing?.discount_amount || 0);
      setAdminNotes(res.data.admin_notes || "");
      setLocked(res.data.pricing?.locked || false);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin/login");
      else toast.error("Failed to load quote");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  useEffect(() => { fetchQuote(); }, [fetchQuote]);

  const calculatePricing = async () => {
    setCalculating(true);
    try {
      const { data } = await ax.post(`/admin/quotes/${id}/calculate`);
      setPricing(data);
      setEditedLineItems(data.line_items || []);
      setMarginOverride(data.summary?.margin_pct ?? 30);
      toast.success("Pricing calculated");
      fetchQuote();
    } catch { toast.error("Calculation failed"); }
    finally { setCalculating(false); }
  };

  const savePricingOverrides = async () => {
    try {
      await ax.patch(`/admin/quotes/${id}/pricing`, {
        custom_line_items: customItems,
        discount_amount: discountAmt,
        locked,
        admin_notes: adminNotes,
        line_items: editedLineItems,
        summary: recalcSummary,
      });
      toast.success("Changes saved");
    } catch { toast.error("Save failed"); }
  };

  const updateStatus = async (status) => {
    try {
      await ax.patch(`/admin/quotes/${id}`, { status });
      setQuote((prev) => ({ ...prev, status }));
      toast.success(`Status: ${status}`);
    } catch { toast.error("Update failed"); }
  };

  const downloadPDF = async () => {
    try {
      const response = await ax.get(`/admin/quotes/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `NGQ-${id.slice(-6).toUpperCase()}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("PDF generation failed"); }
  };

  const addCustomItem = () => setCustomItems([...customItems, { description: "", amount: 0 }]);
  const removeCustomItem = (i) => setCustomItems(customItems.filter((_, idx) => idx !== i));
  const updateCustomItem = (i, field, val) => {
    const updated = [...customItems];
    updated[i] = { ...updated[i], [field]: field === "amount" ? parseFloat(val) || 0 : val };
    setCustomItems(updated);
  };

  const updateLineItem = (i, field, val) => {
    setEditedLineItems((prev) => prev.map((item, idx) =>
      idx === i ? { ...item, [field]: field === "amount" ? parseFloat(val) || 0 : val } : item
    ));
  };

  const removeLineItem = (i) => {
    setEditedLineItems((prev) => prev.filter((_, idx) => idx !== i));
  };

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] pt-24 flex items-center justify-center text-[#A3A3A3]">Loading...</div>;
  if (!quote) return <div className="min-h-screen bg-[#0A0A0A] pt-24 flex items-center justify-center text-[#A3A3A3]">Quote not found</div>;

  const summary = pricing?.summary || {};
  const lineItems = editedLineItems.length > 0 ? editedLineItems : (pricing?.line_items || []);
  const dims = pricing?.dimensions || {};

  // Recalculate summary from edited line items
  const catMap = { "Materials": "material_cost", "Hardware": "hardware_cost", "Labour": "labour_cost", "Installation": "installation_cost", "Features": "feature_costs", "Engineering": "engineering_cost", "Freight": "freight_cost", "Adjustment": "adjustments" };
  const recalcSummary = (() => {
    if (!lineItems.length || !summary.margin_pct) return summary;
    const sums = {};
    for (const key of Object.values(catMap)) sums[key] = 0;
    for (const li of lineItems) {
      const key = catMap[li.category];
      if (key) sums[key] = (sums[key] || 0) + (li.amount || 0);
    }
    const subtotal = Object.values(sums).reduce((s, v) => s + v, 0);
    const marginPct = marginOverride ?? summary.margin_pct ?? 30;
    const marginAmt = Math.round(subtotal * marginPct) / 100;
    const preGst = subtotal + marginAmt;
    const gstPct = summary.gst_rate_pct || 10;
    const gst = Math.round(preGst * gstPct) / 100;
    return { ...summary, ...sums, subtotal: Math.round(subtotal * 100) / 100, margin_pct: marginPct, margin_amount: Math.round(marginAmt * 100) / 100, pre_gst_total: Math.round(preGst * 100) / 100, gst: Math.round(gst * 100) / 100, total: Math.round((preGst + gst) * 100) / 100 };
  })();

  const customTotal = customItems.reduce((s, i) => s + (i.amount || 0), 0);
  const adjustedTotal = (recalcSummary.total || 0) + customTotal - discountAmt;

  return (
    <div data-testid="quote-detail-page" className="min-h-screen bg-[#0A0A0A] pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin" data-testid="back-to-dashboard" className="p-2 border border-[#2E2E2E] text-[#A3A3A3] hover:border-[#FF5722] hover:text-[#FF5722] transition-colors">
              <ArrowLeft size={16} />
            </Link>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-3" style={{ fontFamily: "'Clash Display', sans-serif" }}>
                Quote NGQ-{id.slice(-6).toUpperCase()}
                <Badge className={`text-[10px] px-2 py-0.5 rounded-none text-white ${statusColors[quote.status] || "bg-gray-600"}`}>
                  {quote.status || "new"}
                </Badge>
              </h1>
              <p className="text-xs text-[#A3A3A3] mt-1">Submitted {formatDate(quote.created_at)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select defaultValue={quote.status || "new"} onValueChange={updateStatus}>
              <SelectTrigger data-testid="status-select" className="w-40 h-9 bg-[#1F1F1F] border-[#2E2E2E] text-white text-xs rounded-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1F1F1F] border-[#2E2E2E]">
                {STATUSES.map((s) => <SelectItem key={s} value={s} className="text-white text-xs hover:bg-[#2E2E2E] focus:bg-[#2E2E2E] focus:text-white">{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <button data-testid="calculate-btn" onClick={calculatePricing} disabled={calculating} className="flex items-center gap-2 bg-[#FF5722] text-white px-4 py-2 text-xs font-semibold hover:bg-[#E64A19] transition-colors disabled:opacity-50">
              <Calculator size={14} />{calculating ? "Calculating..." : "Calculate Pricing"}
            </button>
            <button data-testid="download-pdf-btn" onClick={downloadPDF} className="flex items-center gap-2 border border-[#2E2E2E] text-[#A3A3A3] px-4 py-2 text-xs font-semibold hover:border-[#FF5722] hover:text-[#FF5722] transition-colors">
              <Download size={14} />PDF
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left - Customer & Specs */}
          <div className="space-y-6">
            <div className="bg-[#141414] border border-[#2E2E2E] p-5">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4" style={{ fontFamily: "'Clash Display', sans-serif" }}>Customer</h3>
              <div className="space-y-2 text-sm">
                <div><span className="text-[#666]">Name: </span><span className="text-white font-medium">{quote.name}</span></div>
                {quote.company && <div><span className="text-[#666]">Company: </span><span className="text-white">{quote.company}</span></div>}
                <div><span className="text-[#666]">Email: </span><span className="text-white">{quote.email}</span></div>
                {quote.phone && <div><span className="text-[#666]">Phone: </span><span className="text-white">{quote.phone}</span></div>}
              </div>
            </div>

            <div className="bg-[#141414] border border-[#2E2E2E] p-5">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4" style={{ fontFamily: "'Clash Display', sans-serif" }}>Specifications</h3>
              {(quote.items && quote.items.length > 0 ? quote.items : [quote]).map((item, idx) => (
                <div key={idx} className={idx > 0 ? "mt-4 pt-4 border-t border-[#2E2E2E]" : ""}>
                  {(quote.items?.length || 0) > 1 && <p className="text-[#FF5722] text-xs font-bold uppercase tracking-wider mb-2">Item {idx + 1}</p>}
                  <div className="space-y-2 text-sm">
                    <div><span className="text-[#666]">Product: </span><span className="text-white">{(item.product_type || "").replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</span></div>
                    <div><span className="text-[#666]">Layout: </span><span className="text-white">{(item.layout || "").replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</span></div>
                    <div><span className="text-[#666]">Dimensions: </span><span className="text-white">{dims.height || item.height}m x {dims.width || item.width}m{(dims.depth || item.depth) ? ` x ${dims.depth || item.depth}m` : ""}</span></div>
                    <div><span className="text-[#666]">Qty: </span><span className="text-white">{item.quantity || 1}</span></div>
                    <div><span className="text-[#666]">Material: </span><span className="text-white">{(item.material || "").replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</span></div>
                    {item.color && <div><span className="text-[#666]">Colour: </span><span className="text-white capitalize">{item.color}</span></div>}
                    <div><span className="text-[#666]">Operation: </span><span className="text-white capitalize">{item.operation || "—"}</span></div>
                    <div><span className="text-[#666]">Mounting: </span><span className="text-white">{(item.mounting || "").replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase()) || "—"}</span></div>
                    {(item.features || []).length > 0 && <div><span className="text-[#666]">Features: </span><span className="text-white">{item.features.map(f => f.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase())).join(", ")}</span></div>}
                    {item.entry_point && item.entry_point !== "none" && <div><span className="text-[#666]">Entry: </span><span className="text-white capitalize">{item.entry_point.replace(/-/g, " ")}</span></div>}
                  </div>
                </div>
              ))}
            </div>

            {quote.notes && (
              <div className="bg-[#141414] border border-[#2E2E2E] p-5">
                <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-3" style={{ fontFamily: "'Clash Display', sans-serif" }}>Customer Notes</h3>
                <p className="text-sm text-[#A3A3A3] whitespace-pre-wrap">{quote.notes}</p>
              </div>
            )}

            <div className="bg-[#141414] border border-[#2E2E2E] p-5">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-3" style={{ fontFamily: "'Clash Display', sans-serif" }}>Admin Notes</h3>
              <textarea
                data-testid="admin-notes-input"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={3}
                placeholder="Internal notes..."
                className="w-full bg-[#1F1F1F] border border-[#2E2E2E] text-white placeholder:text-[#666] focus:ring-1 focus:ring-[#FF5722] focus:border-[#FF5722] rounded-none p-3 text-sm resize-none outline-none"
              />
            </div>
          </div>

          {/* Right - Pricing Breakdown */}
          <div className="lg:col-span-2 space-y-6">
            {!pricing ? (
              <div className="bg-[#141414] border border-[#2E2E2E] p-12 text-center">
                <DollarSign size={32} className="mx-auto mb-4 text-[#666]" />
                <p className="text-[#A3A3A3] mb-4">No pricing calculated yet</p>
                <button onClick={calculatePricing} disabled={calculating} className="bg-[#FF5722] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#E64A19] transition-colors disabled:opacity-50">
                  <Calculator size={14} className="inline mr-2" />{calculating ? "Calculating..." : "Calculate Now"}
                </button>
              </div>
            ) : (
              <>
                {/* Line Items */}
                <div className="bg-[#141414] border border-[#2E2E2E]">
                  <div className="p-4 border-b border-[#2E2E2E] flex items-center justify-between">
                    <h3 className="text-sm font-bold text-white uppercase tracking-wider" style={{ fontFamily: "'Clash Display', sans-serif" }}>Cost Breakdown</h3>
                    {locked ? <Lock size={14} className="text-[#FF5722]" /> : <Unlock size={14} className="text-[#666]" />}
                  </div>
                  <div className="divide-y divide-[#1F1F1F]">
                    <div className="grid grid-cols-12 px-4 py-2 text-[10px] text-[#666] uppercase tracking-wider font-semibold items-center gap-1">
                      <div className="col-span-2">Category</div>
                      <div className="col-span-6">Description</div>
                      <div className="col-span-3 text-right">Amount</div>
                      <div className="col-span-1"></div>
                    </div>
                    {lineItems.map((item, i) => (
                      item.category === "---" ? (
                        <div key={`li-${i}`} className="px-4 py-2 bg-[#1A1A1A] border-l-2 border-[#FF5722]">
                          <span className="text-[#FF5722] text-xs font-bold uppercase tracking-wider">{item.description}</span>
                        </div>
                      ) : (
                        <div key={`li-${i}`} className="grid grid-cols-12 px-4 py-1.5 text-sm hover:bg-[#1F1F1F] items-center gap-1">
                          <div className="col-span-2">
                            <span className="text-[#A3A3A3] text-xs">{item.category}</span>
                          </div>
                          <div className="col-span-6">
                            <Input
                              data-testid={`li-desc-${i}`}
                              value={item.description}
                              onChange={(e) => updateLineItem(i, "description", e.target.value)}
                              className="bg-transparent border-transparent hover:border-[#2E2E2E] focus:border-[#FF5722] text-white text-xs h-8 rounded-none px-2"
                            />
                          </div>
                          <div className="col-span-3 flex items-center gap-1">
                            <span className="text-[#666] text-xs">$</span>
                            <Input
                              type="number"
                              data-testid={`li-amount-${i}`}
                              value={item.amount}
                              onChange={(e) => updateLineItem(i, "amount", e.target.value)}
                              className="bg-transparent border-transparent hover:border-[#2E2E2E] focus:border-[#FF5722] text-white text-xs h-8 rounded-none px-2 text-right"
                            />
                          </div>
                          <div className="col-span-1 flex items-center justify-end">
                            <button data-testid={`li-remove-${i}`} onClick={() => removeLineItem(i)} className="text-[#666] hover:text-red-400 transition-colors p-1">
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                      )
                    ))}
                    {/* Custom Items */}
                    {customItems.map((ci, i) => (
                      <div key={`ci-${i}`} className="grid grid-cols-12 px-4 py-2 text-sm bg-[#1A1A1A]">
                        <div className="col-span-3 text-[#FF5722] text-xs pt-1">Custom</div>
                        <div className="col-span-5">
                          <Input value={ci.description} onChange={(e) => updateCustomItem(i, "description", e.target.value)} placeholder="Description" className="bg-[#1F1F1F] border-[#2E2E2E] text-white text-xs h-8 rounded-none" />
                        </div>
                        <div className="col-span-3 flex items-center gap-1">
                          <span className="text-[#666] text-xs">$</span>
                          <Input type="number" value={ci.amount} onChange={(e) => updateCustomItem(i, "amount", e.target.value)} className="bg-[#1F1F1F] border-[#2E2E2E] text-white text-xs h-8 rounded-none" />
                        </div>
                        <div className="col-span-1 flex items-center justify-end">
                          <button onClick={() => removeCustomItem(i)} className="text-red-500 hover:text-red-400"><Trash2 size={14} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-4 border-t border-[#2E2E2E]">
                    <button data-testid="add-line-item-btn" onClick={addCustomItem} className="flex items-center gap-2 text-[#FF5722] text-xs font-semibold hover:text-[#E64A19]">
                      <Plus size={14} />Add Custom Line Item
                    </button>
                  </div>
                </div>

                {/* Totals & Controls */}
                <div className="bg-[#141414] border border-[#2E2E2E] p-5">
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4" style={{ fontFamily: "'Clash Display', sans-serif" }}>Quote Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-[#A3A3A3]">Materials</span><span className="text-white">{fmt(recalcSummary.material_cost)}</span></div>
                    <div className="flex justify-between"><span className="text-[#A3A3A3]">Hardware</span><span className="text-white">{fmt(recalcSummary.hardware_cost)}</span></div>
                    <div className="flex justify-between"><span className="text-[#A3A3A3]">Labour</span><span className="text-white">{fmt(recalcSummary.labour_cost)}</span></div>
                    <div className="flex justify-between"><span className="text-[#A3A3A3]">Installation</span><span className="text-white">{fmt(recalcSummary.installation_cost)}</span></div>
                    {recalcSummary.feature_costs > 0 && <div className="flex justify-between"><span className="text-[#A3A3A3]">Features</span><span className="text-white">{fmt(recalcSummary.feature_costs)}</span></div>}
                    {recalcSummary.engineering_cost > 0 && <div className="flex justify-between"><span className="text-[#A3A3A3]">Engineering</span><span className="text-white">{fmt(recalcSummary.engineering_cost)}</span></div>}
                    <div className="flex justify-between"><span className="text-[#A3A3A3]">Freight</span><span className="text-white">{fmt(recalcSummary.freight_cost)}</span></div>
                    {recalcSummary.adjustments !== 0 && <div className="flex justify-between"><span className="text-[#A3A3A3]">Adjustments</span><span className={recalcSummary.adjustments < 0 ? "text-green-400" : "text-white"}>{fmt(recalcSummary.adjustments)}</span></div>}
                    <div className="border-t border-[#2E2E2E] pt-2 flex justify-between"><span className="text-[#A3A3A3]">Subtotal</span><span className="text-white font-medium">{fmt(recalcSummary.subtotal)}</span></div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-[#A3A3A3]">Margin</span>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          data-testid="margin-pct-input"
                          value={marginOverride ?? recalcSummary.margin_pct ?? ""}
                          onChange={(e) => setMarginOverride(parseFloat(e.target.value) || 0)}
                          className="w-16 bg-[#1F1F1F] border-[#2E2E2E] text-white text-sm h-7 rounded-none px-2 text-right"
                          min="0"
                          max="100"
                        />
                        <span className="text-[#666] text-xs">%</span>
                        <span className="text-white ml-2">{fmt(recalcSummary.margin_amount)}</span>
                      </div>
                    </div>
                    {customTotal > 0 && <div className="flex justify-between"><span className="text-[#FF5722]">Custom Items</span><span className="text-[#FF5722]">{fmt(customTotal)}</span></div>}

                    <div className="flex items-center justify-between gap-4 pt-2">
                      <Label className="text-[#A3A3A3] text-sm">Discount ($)</Label>
                      <Input type="number" data-testid="discount-input" value={discountAmt} onChange={(e) => setDiscountAmt(parseFloat(e.target.value) || 0)} className="w-28 bg-[#1F1F1F] border-[#2E2E2E] text-white text-sm h-8 rounded-none" />
                    </div>

                    <div className="flex justify-between"><span className="text-[#A3A3A3]">Ex-GST</span><span className="text-white">{fmt(recalcSummary.pre_gst_total + customTotal - discountAmt)}</span></div>
                    <div className="flex justify-between"><span className="text-[#A3A3A3]">GST ({recalcSummary.gst_rate_pct}%)</span><span className="text-white">{fmt((recalcSummary.pre_gst_total + customTotal - discountAmt) * (recalcSummary.gst_rate_pct / 100))}</span></div>

                    <div className="border-t-2 border-[#FF5722] pt-3 flex justify-between items-center">
                      <span className="text-white font-bold text-lg" style={{ fontFamily: "'Clash Display', sans-serif" }}>TOTAL</span>
                      <span className="text-[#FF5722] font-bold text-xl" style={{ fontFamily: "'Clash Display', sans-serif" }}>{fmt(adjustedTotal + (recalcSummary.pre_gst_total + customTotal - discountAmt) * (recalcSummary.gst_rate_pct / 100) - recalcSummary.gst)}</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                  <button data-testid="save-changes-btn" onClick={savePricingOverrides} className="flex items-center gap-2 bg-[#FF5722] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#E64A19] transition-colors">
                    <Save size={14} />Save Changes
                  </button>
                  <button onClick={() => { setLocked(!locked); }} className={`flex items-center gap-2 border px-5 py-2.5 text-sm font-semibold transition-colors ${locked ? "border-[#FF5722] text-[#FF5722]" : "border-[#2E2E2E] text-[#A3A3A3] hover:border-[#FF5722]"}`}>
                    {locked ? <><Lock size={14} />Locked</> : <><Unlock size={14} />Lock Pricing</>}
                  </button>
                  <button data-testid="download-pdf-btn-2" onClick={downloadPDF} className="flex items-center gap-2 border border-[#2E2E2E] text-[#A3A3A3] px-5 py-2.5 text-sm font-semibold hover:border-[#FF5722] hover:text-[#FF5722] transition-colors">
                    <FileText size={14} />Generate PDF
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
